// Contracts — аргументы инструментов ассистента (ADR-0052).
//
// Вход инструмента сочиняет МОДЕЛЬ. Это недоверенный источник ровно в той же
// мере, что и чужой HTTP-клиент: на кривом ходе она вернёт число там, где ждали
// строку, или пропустит обязательное поле. Без рантайм-проверки такое значение
// уезжает прямо в `.bind()` D1 — падает в глубине или, хуже, отрабатывает не так.
//
// Схемы здесь — вторая половина пары к рукописному JSON Schema в tools.ts
// воркера: тот описывает инструмент МОДЕЛИ, эта проверяет то, что модель
// прислала. Zod→JSON Schema в проекте нет, и тащить зависимость ради двух
// строк на инструмент не стоит — но расходиться им нельзя, они правятся вместе.
import { z } from 'zod';

export const DevPipelineArgsSchema = z.object({
  kind: z.string().trim().max(40).optional(),
  locale: z.string().trim().max(16).optional(),
});
export type DevPipelineArgs = z.infer<typeof DevPipelineArgsSchema>;

export const DevCardArgsSchema = z.object({
  query: z.string().trim().min(1).max(120),
});
export type DevCardArgs = z.infer<typeof DevCardArgsSchema>;

export const DevActivityArgsSchema = z.object({
  range: z.enum(['day', 'week', 'all']).default('day'),
});
export type DevActivityArgs = z.infer<typeof DevActivityArgsSchema>;

export const DevRulesArgsSchema = z.object({
  kind: z.string().trim().max(40).optional(),
});
export type DevRulesArgs = z.infer<typeof DevRulesArgsSchema>;

export const NoArgsSchema = z.object({}).passthrough();

export const CreateIntakeArgsSchema = z.object({
  kind: z.enum(['bug', 'feature', 'question', 'ops', 'task_spec']),
  area: z.string().trim().max(80).default(''),
  title: z.string().trim().min(1).max(200),
  detail: z.string().trim().max(4000).default(''),
  severity: z.enum(['low', 'med', 'high']).optional(),
  // Контур, которого касается просьба. От него зависит, можно ли вообще
  // отдать задачу машине: автополоса живёт только в work.
  contour: z.enum(['work', 'pothos', 'eyeq', 'bot', 'other']).default('other'),
  // ДЕШЁВАЯ оценка сложности прямо в разговоре: ассистент кода не видит, но
  // видит формулировку. На машине она перепроверяется по репозиторию и может
  // быть только поднята — см. workqueue.ts.
  complexity: z.number().int().min(1).max(5).optional(),
});
export type CreateIntakeArgs = z.infer<typeof CreateIntakeArgsSchema>;

export const ListIntakeArgsSchema = z.object({
  mine: z.boolean().default(true),
});
export type ListIntakeArgs = z.infer<typeof ListIntakeArgsSchema>;

// Роли, выдаваемые через бота, перечислены ЗДЕСЬ, а не только в промпте:
// промпт объясняет модели, что делать, схема не пускает всё остальное.
export const GrantAccessArgsSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(160),
  capability: z.enum(['qa', 'dev', 'designer', 'photographer']),
  display_name: z.string().trim().max(80).optional(),
});
export type GrantAccessArgs = z.infer<typeof GrantAccessArgsSchema>;

// Ссылка-приглашение. Человек открывает её, входит — и доступ уже есть.
// Заводить кандидата вторым шагом после первого входа больше не нужно.
export const InviteLinkArgsSchema = z.object({
  target: z.enum(['qa_training', 'qa', 'dev', 'designer', 'photographer']),
  // Обязателен для всех целей КРОМЕ тренировки: у боевых ролей ссылка
  // именная, иначе многоразовая ссылка стала бы самообслуживанием в прод.
  email: z.string().trim().toLowerCase().email().max(160).optional(),
});
export type InviteLinkArgs = z.infer<typeof InviteLinkArgsSchema>;

// Перевод QA между тренировкой и боевой очередью. Двухшаговый: без
// confirm=true инструмент только показывает, что изменится.
export const SwitchQaModeArgsSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(160),
  mode: z.enum(['training', 'real']),
  confirm: z.boolean().optional(),
});
export type SwitchQaModeArgs = z.infer<typeof SwitchQaModeArgsSchema>;
